import React, { useState, useEffect, useMemo } from 'react';
import { View, Text, TouchableOpacity, Platform, Linking, ActivityIndicator, ScrollView } from 'react-native';
import { useRoute } from '@react-navigation/native';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { getAuth } from 'firebase/auth';
import { globalStyles } from '../styles/globalStyles';
import { BackgroundVideo } from '../components/BackgroundVideo';
import { SUBSCRIPTION_PLAN_LIST, formatCentsAsDollars } from '../constants/subscriptionPlans';
import { REFERRAL_DISCOUNT_CENTS } from '../constants/referrals';
import { useTheme } from '../contexts/ThemeContext';
import { getDeployTarget } from '../config/environment';
import { getUserSubscriptionPlanId } from '../utils/subscriptionEntitlements';
import { useUser } from '../components/LoginPersistence';
import { fetchReferralPreview } from '../utils/referralService';

function readReferralCode(route) {
  const fromParams = route?.params?.ref;
  if (fromParams) return String(fromParams).trim();
  if (Platform.OS === 'web' && typeof window !== 'undefined') {
    return String(new URLSearchParams(window.location.search).get('ref') || '').trim();
  }
  return '';
}

export function Plans({ navigation }) {
  const route = useRoute();
  const [loadingPlanId, setLoadingPlanId] = useState(null);
  const [error, setError] = useState('');
  const { user } = useUser();
  // Derived on render so an active subscriber never sees a Subscribe button flash first.
  const userSubscriptionPlanId = user ? getUserSubscriptionPlanId(user) : null;
  const { isDarkMode } = useTheme();
  const referralCode = useMemo(() => readReferralCode(route), [route?.params?.ref]);
  const [referralPreview, setReferralPreview] = useState(null);
  const [referralLoading, setReferralLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;
    if (!referralCode) {
      setReferralPreview(null);
      return undefined;
    }
    setReferralLoading(true);
    fetchReferralPreview(referralCode)
      .then((data) => {
        if (!cancelled) setReferralPreview(data);
      })
      .catch(() => {
        if (!cancelled) setReferralPreview({ valid: false });
      })
      .finally(() => {
        if (!cancelled) setReferralLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [referralCode]);

  const handleSubscribe = async (planId) => {
    const auth = getAuth();
    const currentUser = auth.currentUser;
    if (!currentUser) {
      navigation.navigate('Login');
      return;
    }

    setError('');
    setLoadingPlanId(planId);
    try {
      const deployTarget = getDeployTarget();
      const origin =
        Platform.OS === 'web' && typeof window !== 'undefined' && window.location?.origin
          ? window.location.origin
          : null;
      const functions = getFunctions();
      const createCheckoutSession = httpsCallable(functions, 'createCheckoutSession');
      const result = await createCheckoutSession({
        planId,
        deployTarget,
        ...(referralPreview?.valid && referralCode
          ? { referralCode: referralPreview.code || referralCode }
          : {}),
        ...(origin
          ? {
              successUrl: `${origin}/subscription-success`,
              cancelUrl: `${origin}/plans${referralCode ? `?ref=${encodeURIComponent(referralCode)}` : ''}`,
            }
          : {}),
      });
      const url = result?.data?.url;
      if (!url) {
        throw new Error('Checkout URL was not returned. Check Stripe price configuration.');
      }
      if (Platform.OS === 'web') {
        window.location.href = url;
      } else {
        await Linking.openURL(url);
      }
    } catch (err) {
      console.error('Checkout error:', err);
      setError(err?.message || 'Could not start checkout. Please try again.');
    } finally {
      setLoadingPlanId(null);
    }
  };

  const referralActive = referralPreview?.valid === true;

  return (
    <View style={globalStyles.container}>
      <BackgroundVideo />
      <ScrollView contentContainerStyle={[globalStyles.overlay, globalStyles.plansContent]}>
        <View style={globalStyles.plansHeader}>
          <Text style={[globalStyles.plansTitle, isDarkMode && { color: '#ffffff' }]}>VRC Membership</Text>
          <Text style={[globalStyles.plansSubtitle, isDarkMode && { color: '#ffffff' }]}>
            One plan — Map and Calendar included
          </Text>
        </View>

        {referralCode ? (
          <View
            style={{
              marginBottom: 16,
              padding: 14,
              borderRadius: 10,
              borderWidth: 1,
              borderColor: referralActive ? 'rgba(46,204,113,0.5)' : 'rgba(231,76,60,0.45)',
              maxWidth: 640,
            }}
          >
            {referralLoading ? (
              <ActivityIndicator color="#4e7de0" />
            ) : referralActive ? (
              <>
                <Text style={{ color: isDarkMode ? '#fff' : '#111', fontWeight: '600', marginBottom: 6 }}>
                  Referral applied — {formatCentsAsDollars(REFERRAL_DISCOUNT_CENTS)} off your first month
                </Text>
                <Text style={{ color: isDarkMode ? 'rgba(255,255,255,0.75)' : '#444', fontSize: 14 }}>
                  First month{' '}
                  {formatCentsAsDollars(referralPreview.firstMonthCents)} instead of{' '}
                  {formatCentsAsDollars(referralPreview.planPriceCents)} (code {referralPreview.code})
                </Text>
              </>
            ) : (
              <Text style={{ color: '#ffb4b4' }}>
                Referral code “{referralCode}” is not active. Subscribe at full price or ask for a new link.
              </Text>
            )}
          </View>
        ) : null}

        {error ? (
          <Text style={[globalStyles.plansSubtitle, { color: '#ffb4b4', marginBottom: 16, maxWidth: 640 }]}>
            {error}
          </Text>
        ) : null}

        <View style={globalStyles.plansRow}>
          {SUBSCRIPTION_PLAN_LIST.map((plan) => {
            const busy = loadingPlanId === plan.id;
            const displayCents =
              referralActive ? referralPreview.firstMonthCents : plan.priceCents;
            // Any active subscriber (legacy $22/$44 included) manages their plan instead of a second checkout.
            const isCurrent = !!userSubscriptionPlanId;
            return (
              <View
                key={plan.id}
                style={[globalStyles.planCard, globalStyles.planCardHighlighted]}
              >
                {isCurrent && (
                  <View style={globalStyles.currentPlanBadge}>✓ Current Plan</View>
                )}
                <View style={globalStyles.planHeader}>
                  <Text style={globalStyles.planName}>{plan.name}</Text>
                  <View style={globalStyles.priceContainer}>
                    <Text style={globalStyles.priceCurrency}>$</Text>
                    <Text style={globalStyles.priceAmount}>{Math.round(displayCents / 100)}</Text>
                    <Text style={globalStyles.pricePeriod}>
                      {referralActive ? ' first month' : plan.periodLabel}
                    </Text>
                  </View>
                  {referralActive ? (
                    <Text style={globalStyles.planDiscountHint}>
                      Then {plan.priceLabel}/mo · referral −{formatCentsAsDollars(REFERRAL_DISCOUNT_CENTS)}
                    </Text>
                  ) : (
                    <Text style={globalStyles.planDiscountHint}>
                      Earn Credits from approved map contributions
                    </Text>
                  )}
                </View>

                <View style={globalStyles.featuresContainer}>
                  <Text style={globalStyles.featuresTitle}>What's Included:</Text>
                  {plan.features.map((feature) => (
                    <View key={feature} style={globalStyles.featureItem}>
                      <Text style={globalStyles.featureIcon}>✓</Text>
                      <Text style={globalStyles.featureText}>{feature}</Text>
                    </View>
                  ))}
                </View>

                <Text style={[globalStyles.featureText, { opacity: 0.85, marginBottom: 16, textAlign: 'center' }]}>
                  {plan.blurb}
                </Text>

                {isCurrent ? (
                  <TouchableOpacity
                    style={[globalStyles.subscribeButton, busy && { opacity: 0.7 }]}
                    onPress={() => navigation.navigate('Wallet', { tab: 'membership' })}
                    disabled={!!loadingPlanId}
                  >
                    {busy ? (
                      <ActivityIndicator color="#4e7de0" />
                    ) : (
                      <Text style={globalStyles.subscribeButtonText}>Manage Subscription</Text>
                    )}
                  </TouchableOpacity>
                ) : (
                  <TouchableOpacity
                    style={[globalStyles.subscribeButton, busy && { opacity: 0.7 }]}
                    onPress={() => handleSubscribe(plan.id)}
                    disabled={!!loadingPlanId}
                  >
                    {busy ? (
                      <ActivityIndicator color="#4e7de0" />
                    ) : (
                      <Text style={globalStyles.subscribeButtonText}>
                        Subscribe — {formatCentsAsDollars(displayCents)}
                        {referralActive ? ' first month' : '/mo'}
                      </Text>
                    )}
                  </TouchableOpacity>
                )}

                <Text style={globalStyles.guaranteeText}>Cancel anytime</Text>
              </View>
            );
          })}
        </View>
      </ScrollView>
    </View>
  );
}
